import { createContext, useEffect, useCallback, useRef, useMemo } from "react";
// firestore
import { doc, setDoc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
//
import axios from "../../utils/axios";
// settings
import actionTypes from "../../common/actionTypes";
import {
  DATABASE_STATUTES,
  FREE,
  STARTER,
  MONTH_INTERVAL,
  SHOP_PLAN_TYPE,
  CUSTOM,
  UNLIMITED_PREORDERS,
} from "../../common/constants";
import { initialState as shopInitialState } from "../../common/shopConstants";
// context
import useAuthContext from "../../hooks/useAuthContext";
// reducers
import { shopReducer } from "../../reducers/shopReducer";
// hooks
import useLocales from "../../hooks/useLocales";
import useLocalStorage from "../../hooks/useLocalStorage";
import useCustomReducer from "../../hooks/useCustomReducer";
// schemas
import {
  getAllScriptTags,
  getRemoveScriptTagMutation,
  getScriptTagCreateMutation,
  getSubscribeCustomMutation,
  getSubscribeMutation,
  getUnsubscribedMutation,
} from "./schemas";
import { deepEqual } from "../../utils/compareObjects";

const SHOPS = "Shops";
const SHOPIFY_PROXY_URL = "shopify-proxy";
const SHOPIFY_VALIDATE_SUBSCRIPTION = "shopify/subscription/validate";
const SHOP_IS_THEME_EXTENSION_APP_ENABLED_URL = "shopify/theme/appExtension";
const SHOP_REVIEW_URL = "shop/review";
const SHOP_DOMAIN = "/shop/domain";
const SHOP_DOMAIN_VALIDATE = "/shop/domain/validate";

export const getSubscriptionParams = ({ shopId, price, type, interval }) => {
  const name = type === STARTER ? "Timesact Starter" : "Timesact Premium";

  return {
    shopId,
    shopName: shopId.split(".")[0],
    name,
    price,
    plan: type,
    interval,
  };
};

const initialState = {
  shop: { ...shopInitialState },
  error: null,
  status: DATABASE_STATUTES.NOT_LOADED,
  subscribeConfirmUrl: "",
};

const ShopContext = createContext({
  ...initialState,
  setShopLng: () => {},
  subscribe: () => Promise.resolve(),
  subscribeCustom: () => Promise.resolve(),
  unsubscribe: () => Promise.resolve(),
  isValidSubscription: () => Promise.resolve(),
  isValidSubscriptionCustom: () => Promise.resolve(),
  addScriptTag: () => Promise.resolve(),
  removeScriptTag: () => Promise.resolve(),
  updateSelectors: () => Promise.resolve(),
  switchShowAskOrEmojiModal: () => {},
  updatePreferences: () => {},
  sendReview: () => Promise.resolve(),
  validateIsThemeExtensionAppEnabled: () => Promise.resolve(),
});

const ShopProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(shopReducer, initialState);

  const { user } = useAuthContext();

  const { onChangeLang } = useLocales();
  const [, setShopPlanType] = useLocalStorage(SHOP_PLAN_TYPE);

  const shopRef = useRef(null);
  const prevShopRef = useRef(null);

  useEffect(() => {
    if (user?.domain) {
      dispatch({
        type: actionTypes.FETCH_SHOP,
      });

      shopRef.current = doc(db, SHOPS, user.domain);

      try {
        return onSnapshot(
          shopRef.current,
          (doc) => {
            const shop = doc.data();

            if (prevShopRef.current?.localeApp !== shop.localeApp) {
              onChangeLang(shop.localeApp);
            }

            if (prevShopRef.current?.planType !== shop.planType) {
              setShopPlanType(shop.planType);
            }

            const isShopEqualPrev = deepEqual(shop, prevShopRef.current, (key) =>
              ["lastProductUpdate", "lastSettingsUpdate"].includes(key)
            );

            if (!isShopEqualPrev) {
              dispatch({
                type: actionTypes.FETCH_SHOP_SUCCESS,
                payload: {
                  shop,
                  uid: user.domain,
                },
              });
            }

            prevShopRef.current = shop;
          },
          (err) => {
            console.log("%c  [Error]:", "color: red; font-weight: bold;", err);
          }
        );
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_SHOP_FAILURE,
          error: error,
        });
        console.log("%c [Error]:", "color: red; font-weight: bold;", error);
      }
    }
  }, [setShopPlanType, user?.domain, dispatch]);

  const validateIsThemeExtensionAppEnabled = useCallback(async () => {
    try {
      dispatch({
        type: actionTypes.VALIDATE_THEME_EXTENSION_APP_ENABLED,
      });

      const res = await axios.get(SHOP_IS_THEME_EXTENSION_APP_ENABLED_URL);
      dispatch({
        type: actionTypes.VALIDATE_THEME_EXTENSION_APP_ENABLED_SUCCESS,
      });
      return res?.data?.enabled;
    } catch (error) {
      dispatch({
        type: actionTypes.VALIDATE_THEME_EXTENSION_APP_ENABLED_FAILURE,
        payload: error,
      });
    }
  }, [dispatch]);

  const setShopLng = (lng) => {
    setDoc(
      shopRef.current,
      {
        localeApp: lng,
      },
      { merge: true }
    );
  };

  const updateSelectors = async (selectors) => {
    try {
      await setDoc(
        shopRef.current,
        {
          selectors,
        },
        { merge: true }
      );
    } catch (error) {
      console.log("%c [Error]", "color: red; font-weight: bold;", error);
    }
  };

  const subscribe = useCallback(
    async (type, { price, interval = MONTH_INTERVAL }) => {
      try {
        const params = getSubscriptionParams({
          shopId: user.domain,
          price,
          type,
          interval,
        });

        const {
          data: { response: { appSubscriptionCreate } = {} },
        } = await axios.post(SHOPIFY_PROXY_URL, {
          query: getSubscribeMutation(params),
        });

        if (appSubscriptionCreate.confirmationUrl) {
          const { confirmationUrl } = appSubscriptionCreate;

          dispatch({
            type: actionTypes.STORE_SUBSCRIBE_CONFIRMATION_URL,
            payload: confirmationUrl,
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    [dispatch, user?.domain]
  );

  const subscribeCustom = useCallback(
    async ({
      price,
      preorders,
      preordersPrice,
      instock,
      instockPrice,
      bisNotifications,
      bisNotificationsPrice,
      interval,
    }) => {
      try {
        const shopName = user.domain.split(".")[0];

        let name = "Timesact Flexible plan";
        if (preorders === UNLIMITED_PREORDERS && price != 180) {
          name = "Timesact Fixed Unlimited";
        }

        const {
          data: { response: { appSubscriptionCreate } = {} },
        } = await axios.post(SHOPIFY_PROXY_URL, {
          query: getSubscribeCustomMutation({
            shopId: user.domain,
            shopName,
            price: price,
            plan: CUSTOM,
            preorders,
            preordersPrice,
            instock,
            instockPrice,
            bisNotifications,
            bisNotificationsPrice,
            interval,
            name,
          }),
        });

        if (appSubscriptionCreate.confirmationUrl) {
          const { confirmationUrl } = appSubscriptionCreate;

          dispatch({
            type: actionTypes.STORE_SUBSCRIBE_CONFIRMATION_URL,
            payload: confirmationUrl,
          });
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [dispatch, user?.domain]
  );

  const unsubscribe = useCallback(async () => {
    if (!state.shop.subscription.chargedId) {
      return false;
    }

    try {
      const {
        data: { response: { appSubscriptionCancel } = {} },
      } = await axios.post(SHOPIFY_PROXY_URL, {
        query: getUnsubscribedMutation(state.shop.subscription.chargedId),
      });

      if (appSubscriptionCancel.appSubscription.status === "CANCELLED") {
        await setDoc(
          doc(db, SHOPS, user.domain),
          {
            isDisabled: state.shop.freePlan.limits.preorders <= state.shop.freePlan.used.preorders,
            isDisabledIS: state.shop.freePlan.limits.instock <= state.shop.freePlan.used.instock,
            isDisabledBIS: state.shop.freePlan.limits.emailsBIS <= state.shop.freePlan.used.emailsBIS,
            subscription: {
              plan: FREE,
              chargeStatus: "cancelled",
              isOldUnlimited: false,
              isUnlimitedPO: false,
              isUnlimitedBIS: false,
              isUnlimitedIS: false,
              price: 0,
              preorders: 0,
              preordersPrice: 0,
              instock: 0,
              instockPrice: 0,
              bisNotifications: 0,
              bisNotificationsPrice: 0,
            },
          },
          { merge: true }
        );

        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  }, [
    state.shop.freePlan?.limits?.emailsBIS,
    state.shop.freePlan?.limits?.instock,
    state.shop.freePlan?.limits?.preorders,
    state.shop.freePlan?.used?.emailsBIS,
    state.shop.freePlan?.used?.instock,
    state.shop.freePlan?.used?.preorders,
    state.shop.subscription?.chargedId,
    user?.domain,
  ]);

  const isValidSubscription = async (shopId, chargeId, planType) => {
    try {
      const res = await axios.get(SHOPIFY_VALIDATE_SUBSCRIPTION, {
        params: {
          shopId,
          chargeId,
          planType,
        },
      });

      if (res.status === 200) {
        return true;
      }
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const isValidSubscriptionCustom = async (
    shopId,
    chargeId,
    planType,
    price,
    preorders,
    preordersPrice,
    instock,
    instockPrice,
    bisNotifications,
    bisNotificationsPrice
  ) => {
    try {
      const res = await axios.get(SHOPIFY_VALIDATE_SUBSCRIPTION, {
        params: {
          shopId,
          chargeId,
          planType,
          price,
          preorders,
          preordersPrice,
          instock,
          instockPrice,
          bisNotifications,
          bisNotificationsPrice,
        },
      });

      if (res.status === 200) {
        return true;
      }
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const addScriptTag = useCallback(
    async (scriptVersion) => {
      try {
        const {
          data: { response: { scriptTagCreate } = {} },
        } = await axios.post(SHOPIFY_PROXY_URL, {
          query: getScriptTagCreateMutation(scriptVersion),
        });

        if (scriptTagCreate && scriptTagCreate.scriptTag && scriptTagCreate.scriptTag.id) {
          await setDoc(
            doc(db, SHOPS, user.domain),
            {
              hasScript: true,
              scriptVersion: "3.0",
            },
            { merge: true }
          );
        }
      } catch (error) {
        console.log(error);
      }
    },
    [user?.domain]
  );

  const removeScriptTag = useCallback(async () => {
    try {
      const {
        data: { response: { scriptTags } = {} },
      } = await axios.post(SHOPIFY_PROXY_URL, {
        query: getAllScriptTags(),
      });

      for (let edge of scriptTags.edges) {
        await axios.post(SHOPIFY_PROXY_URL, {
          query: getRemoveScriptTagMutation(edge.node.id),
        });
      }

      await setDoc(
        doc(db, SHOPS, user.domain),
        {
          hasScript: false,
        },
        { merge: true }
      );
    } catch (error) {
      console.log(error);
    }
  }, [user?.domain]);

  const removeSubscribeConfirmUrl = () => {
    dispatch({
      type: actionTypes.REMOVE_SUBSCRIBE_CONFIRMATION_URL,
    });
  };

  const sendReview = async ({ rating = null, review = "" }) => {
    try {
      dispatch({ type: actionTypes.SEND_REVIEW });

      console.log(rating);
      const payload = {
        tutorial: {
          // use it when user write a review to our backend
          reviewAsked: true,
        },
      };

      const isRatingSet = typeof rating === "number";

      if (isRatingSet && rating < 5) {
        payload.hasBadReviewRisk = true;
        await axios.post(SHOP_REVIEW_URL, {
          stars: rating,
          review,
        });
      }

      await setDoc(shopRef.current, payload, { merge: true });

      dispatch({
        type: actionTypes.SEND_REVIEW_SUCCESS,
      });

      return Promise.resolve();
    } catch (error) {
      console.log("error", error);
      dispatch({
        type: actionTypes.SEND_REVIEW_FAILURE,
        payload: error,
      });

      return Promise.reject();
    }
  };

  const createDNSRecordInDB = async (email) => {
    try {
      const res = await axios.post(`${SHOP_DOMAIN}`, {
        fromEmail: email,
      });

      console.log("email createDNSRecordInDB", email);
      console.log("res", res);

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.error || "Failed to create DNS records");
      }

      // Return the DNS records from the server response
      return res.data.data.dnsRecords || [];
    } catch (error) {
      console.error("Error when creating DNS records:", error);

      // Handle specific server errors
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }

      throw error;
    }
  };

  const confirmDNSRecord = async (dnsRecords) => {
    try {
      const res = await axios.post(`${SHOP_DOMAIN_VALIDATE}`);

      console.log("DNS validation response:", res);
      console.log("confirmDNSRecord dnsRecords:", dnsRecords);

      if (!res.data || !res.data.success) {
        throw new Error(res.data?.error || "DNS validation failed");
      }

      // Refresh the shop data to get updated DNS status
      const docSnap = await getDoc(shopRef.current);

      if (!docSnap.exists()) {
        console.warn("Shop not found");
        throw new Error("Shop not found");
      }

      const shopData = docSnap.data();
      console.log("Updated shop data:", shopData.customDomain);

      return {
        success: res.data.success,
        isValid: res.data.isValid,
        status: res.data.status,
        validationDetails: res.data.validationDetails,
        updatedDnsRecords: shopData.customDomain?.dnsRecords || [],
      };
    } catch (error) {
      console.error("Error in confirmDNSRecord:", error);

      // Handle specific server errors
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }

      throw error;
    }
  };

  const switchShowAskOrEmojiModal = (state, nameModal) => {
    // hide modal
    if (!state) {
      setDoc(
        shopRef.current,
        {
          tutorial: {
            [nameModal]: false,
          },
        },
        { merge: true }
      );
    }
    // show modal
    if (state) {
      console.log("Hide modal work!!!!, add logic");
      setDoc(
        shopRef.current,
        {
          tutorial: {
            showAskReviewModal: true,
            showEmojiReviewModal: true,
          },
        },
        { merge: true }
      );
    }
  };

  const changeFirstStepToHideModal = async () => {
    try {
      const payload = {
        tutorial: {
          firstStep: true,
        },
      };
      await setDoc(shopRef.current, payload, { merge: true });

      return Promise.resolve();
    } catch (error) {
      console.log("error changeFirstStepToHideModal", error);

      return Promise.reject();
    }
  };

  const updatePreferences = (preferences) => {
    setDoc(
      shopRef.current,
      {
        preferences,
      },
      { merge: true }
    );
  };

  const setTemporallyThemeExtensionAppEnabled = () => {
    dispatch({
      type: actionTypes.SET_TEMPORALLY_THEME_EXTENSION_APP_ENABLED,
    });
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      setShopLng,
      subscribe,
      subscribeCustom,
      unsubscribe,
      isValidSubscription,
      isValidSubscriptionCustom,
      addScriptTag,
      removeScriptTag,
      updateSelectors,
      removeSubscribeConfirmUrl,
      createDNSRecordInDB,
      confirmDNSRecord,
      switchShowAskOrEmojiModal,
      updatePreferences,
      setTemporallyThemeExtensionAppEnabled,
      validateIsThemeExtensionAppEnabled,
      sendReview,
      changeFirstStepToHideModal,
    }),
    [
      addScriptTag,
      removeScriptTag,
      state,
      subscribe,
      subscribeCustom,
      unsubscribe,
      // Unnecessary dependencies
      // updateSelectors,
      // setShopLng,
      // removeSubscribeConfirmUrl,
      // updatePreferences,
      // setTemporallyThemeExtensionAppEnabled,
      // validateIsThemeExtensionAppEnabled,
    ]
  );

  return <ShopContext.Provider value={memoizedState}>{children}</ShopContext.Provider>;
};

export { ShopContext, ShopProvider };
